import { EnvironmentProviders, inject, provideAppInitializer, Provider, Type } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { EnvironmentService } from '@libs/shared/common';
import { AuthContextType, ConnectionAuthProvider } from '@libs/shared/models';
import { AuthHandler, UserAuthHandler } from './auth.model';
import { AzureOAuthWebResultHandlerService } from './azure-oauth-web-result-handler.service';
import {
  AzureOAuthWebService,
  generalAuthProviders,
  llDirectoryUserMsalAuthProviders,
  OAuthAppService,
  OAuthService,
  OAuthWebService,
  userMsalAuthProviders,
} from './login';
import { AuthState } from './state';
import { AppUserAuthHandler, LoginUserAuthHandler, StorageUserAuthHandler } from './user';

export class AuthServiceFactory {
  private static readonly oauthPlatforms: Record<string, Type<OAuthService>> = {
    web: OAuthWebService,
    app: OAuthAppService,
    android: OAuthAppService,
  };

  private static readonly userAuthHandlers: Record<AuthContextType, Type<UserAuthHandler>> = {
    login: LoginUserAuthHandler,
    app: AppUserAuthHandler,
    public: StorageUserAuthHandler,
  };

  public static createProviders(): (Provider | EnvironmentProviders)[] {
    return AuthServiceFactory.getLoginProviders();
  }

  public static getBaseProviders(): (Provider | EnvironmentProviders)[] {
    return [
      {
        provide: UserAuthHandler,
        useFactory: () => AuthServiceFactory.createAuthHandler(),
      },
      {
        provide: ConnectionAuthProvider,
        useExisting: AuthState,
      },
    ];
  }

  private static getLoginProviders(): (Provider | EnvironmentProviders)[] {
    const platform: string = Capacitor.getPlatform();

    let loginProviders: (Provider | EnvironmentProviders)[] = [
      {
        provide: OAuthService,
        useClass: AuthServiceFactory.oauthPlatforms[platform] ?? OAuthAppService,
      },
      ...AuthServiceFactory.getBaseProviders(),
    ];

    if (platform === 'web') {
      loginProviders = loginProviders.concat(AuthServiceFactory.getMsalProviders());
    }

    return loginProviders;
  }

  private static getMsalProviders(): (Provider | EnvironmentProviders)[] {
    return [
      AzureOAuthWebService,
      ...llDirectoryUserMsalAuthProviders,
      ...userMsalAuthProviders,
      ...generalAuthProviders,
      provideAppInitializer(() => void inject(AzureOAuthWebResultHandlerService)),
    ];
  }

  private static createAuthHandler(): AuthHandler {
    const env: EnvironmentService = inject(EnvironmentService);
    const context: AuthContextType = env.app().context as AuthContextType;

    const handlerType: Type<AuthHandler> = AuthServiceFactory.userAuthHandlers[context] ?? StorageUserAuthHandler;

    return new handlerType();
  }
}
